import { css, SerializedStyles } from '@emotion/core';
import { styled } from '@material-ui/core/styles';
import {
  Card,
  ExpansionPanel,
  ExpansionPanelSummary,
  Typography,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Link } from 'react-router-dom';

import { ICustomTheme } from 'interfaces';

type Theme = {
  theme: ICustomTheme;
};

export const StyledLastDeclaration = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: theme.typography.body1.fontSize,
      padding: theme.spacing(2, 3),
    },
    textAlign: 'center',
    '& > p': {
      margin: 0,
    },
    padding: theme.spacing(1, 2),
    fontSize: 15,
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[700],
  }),
);

export const StyledHistoryLink = styled(Link)(
  ({ theme }: { theme: ICustomTheme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: theme.typography.fontWeightBold,
    textDecoration: 'underline',
    '& > svg': {
      marginLeft: theme.spacing(1),
    },
    color: theme.palette.grey[700],
  }),
);

export const StyledDetailsLink = styled(Link)(
  ({ theme }: { theme: ICustomTheme }) => ({
    textDecoration: 'underline',
    color: theme.palette.grey[700],
  }),
);

export const StyledNote = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    color: theme.colors.paletteFromBO.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.caption.fontSize,
    },
  }),
);

export const StyledPanel = styled(ExpansionPanel)(
  ({ theme }: { theme: ICustomTheme }) => ({
    boxShadow: 'none',
    margin: '0 !important',
    '::before': {
      backgroundColor: theme.palette.grey[100],
      opacity: '1 !important',
    },
  }),
);

export const StyledPanelSummary = styled(ExpansionPanelSummary)(
  ({ theme }: { theme: ICustomTheme }) => ({
    minHeight: `${theme.spacing(6)}px !important`,
    '& .MuiExpansionPanelSummary-content': {
      flexGrow: 0,
      margin: `${theme.spacing(1.5, 0)} !important`,
    },
  }),
);

export const StyledTitle = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    [theme.breakpoints.down('sm')]: {
      color: theme.colors.paletteFromBO.main,
      fontSize: theme.typography.h5.fontSize,
      marginBottom: theme.spacing(2),
    },
  }),
);

export const getCssCustomStepper =
  (currentStep: number) =>
  (theme: ICustomTheme): SerializedStyles =>
    css({
      [theme.breakpoints.down('xs')]: {
        marginLeft: -theme.spacing(currentStep * 19),
        transition: theme.transitions.create('margin-left'),
      },
      '& .MuiStep-completed': {
        '& .MuiStepConnector-line': {
          background: theme.colors.paletteFromBO.main,
        },
      },
    });

export const StyledCard = styled(Card)(
  ({ theme }: { theme: ICustomTheme }) => ({
    boxShadow: theme.shadows[3],
    color: theme.colors.grey.dark,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(5),
    padding: `${theme.spacing(6)}px 9.25vw`,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(2),
    },
  }),
);

export const StyledTeaser = styled('div')(
  () => ({
    textAlign: 'initial',
  }),
);

export const StyledCenteredSkeleton = styled(Skeleton)(() => ({
  margin: '0 auto',
}));

export const StyledDesktopSkeleton = styled(Skeleton)(
  ({ theme }: { theme: ICustomTheme }) => ({
    margin: `${theme.spacing(3.5)}px auto ${theme.spacing(5)}px`,
  }),
);

export const StyledMobileSkeleton = styled(Skeleton)(
  ({ theme }: { theme: ICustomTheme }) => ({
    margin: `${theme.spacing(3.5)}px auto ${theme.spacing(1)}px`,
  }),
);
